<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-modal"
      @click.self="onBackdropClick"
    >
      <div class="absolute inset-0 bg-bg-overlay" aria-hidden="true" @click="onBackdropClick" />
      <aside
        ref="drawerRef"
        class="absolute inset-y-0 right-0 flex w-full flex-col overflow-hidden border-l border-border bg-bg-elevated shadow-modal lg:w-3/4"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
      >
        <header class="flex shrink-0 items-center justify-between gap-3 border-b border-border bg-bg-surface px-4 py-3">
          <div class="min-w-0">
            <slot name="eyebrow" />
            <h2 :id="titleId" class="truncate text-lg font-semibold text-text-primary">{{ title }}</h2>
          </div>
          <AppButton
            :variant="ButtonVariant.Ghost"
            :size="ButtonSize.Sm"
            :aria-label="messages.closeTitle(title)"
            @click="requestClose"
          >
            <template #icon-left><AppIcon :name="IconName.X" /></template>
            {{ messages.close }}
          </AppButton>
        </header>
        <div class="min-h-0 flex-1 overflow-auto">
          <slot />
        </div>
      </aside>
    </div>
  </Teleport>
</template>

<script lang="ts">
interface AttributeSnapshot {
  readonly inert: string | null
  readonly ariaHidden: string | null
}

interface DrawerLayer {
  readonly id: symbol
  readonly root: HTMLElement
}

let drawerLayers: ReadonlyArray<DrawerLayer> = []
const isolatedElements = new Map<HTMLElement, AttributeSnapshot>()
const bodyLockOwners = new Set<symbol>()
let bodyOverflowBeforeLock = ''

function isolateElement(element: HTMLElement): void {
  if (!isolatedElements.has(element)) {
    isolatedElements.set(element, {
      inert: element.getAttribute('inert'),
      ariaHidden: element.getAttribute('aria-hidden'),
    })
  }
  element.setAttribute('inert', '')
  element.setAttribute('aria-hidden', 'true')
}

function restoreElement(element: HTMLElement): void {
  const snapshot = isolatedElements.get(element)
  if (!snapshot) return
  if (snapshot.inert === null) element.removeAttribute('inert')
  else element.setAttribute('inert', snapshot.inert)
  if (snapshot.ariaHidden === null) element.removeAttribute('aria-hidden')
  else element.setAttribute('aria-hidden', snapshot.ariaHidden)
  isolatedElements.delete(element)
}

function refreshDrawerIsolation(): void {
  const elementsToIsolate = new Set<HTMLElement>()
  if (drawerLayers.length > 0) {
    const appShell = document.getElementById('app')
    if (appShell) elementsToIsolate.add(appShell)
    const topLayer = drawerLayers[drawerLayers.length - 1]
    for (const layer of drawerLayers) {
      if (layer !== topLayer) elementsToIsolate.add(layer.root)
    }
  }
  for (const element of isolatedElements.keys()) {
    if (!elementsToIsolate.has(element)) restoreElement(element)
  }
  for (const element of elementsToIsolate) isolateElement(element)
}

function registerDrawerLayer(id: symbol, root: HTMLElement): void {
  drawerLayers = [...drawerLayers.filter((layer) => layer.id !== id), { id, root }]
  refreshDrawerIsolation()
}

function unregisterDrawerLayer(id: symbol): void {
  if (!drawerLayers.some((layer) => layer.id === id)) return
  drawerLayers = drawerLayers.filter((layer) => layer.id !== id)
  refreshDrawerIsolation()
}

function lockBody(id: symbol): void {
  if (bodyLockOwners.has(id)) return
  if (bodyLockOwners.size === 0) bodyOverflowBeforeLock = document.body.style.overflow
  bodyLockOwners.add(id)
  document.body.style.overflow = 'hidden'
}

function unlockBody(id: symbol): void {
  if (!bodyLockOwners.delete(id) || bodyLockOwners.size > 0) return
  document.body.style.overflow = bodyOverflowBeforeLock
}
</script>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { useFocusTrap } from '@/shared/composables'
import AppButton, { ButtonSize, ButtonVariant } from './AppButton.vue'
import AppIcon, { IconName } from './AppIcon.vue'
import { appDrawerEn } from './locales/en.ts'
import { appDrawerRu } from './locales/ru.ts'

export interface AppDrawerProps {
  readonly modelValue: boolean
  readonly title: string
  readonly closeOnBackdrop?: boolean
  readonly closeOnEsc?: boolean
}

const props = withDefaults(defineProps<AppDrawerProps>(), {
  closeOnBackdrop: true,
  closeOnEsc: true,
})

const catalog = defineLocaleCatalog({ en: appDrawerEn, ru: appDrawerRu })
const messages = useLocaleMessages(catalog)

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
}>()

const drawerRef = ref<HTMLElement | null>(null)
const titleId = `drawer-title-${useId()}`
const { activate, deactivate } = useFocusTrap(drawerRef)
const layerId = Symbol('app-drawer-layer')

function requestClose(): void {
  emit('update:modelValue', false)
}

function onBackdropClick(): void {
  if (props.closeOnBackdrop) requestClose()
}

function onWindowKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || event.defaultPrevented || !props.closeOnEsc) return
  event.preventDefault()
  requestClose()
}

function releaseDrawer(): void {
  unregisterDrawerLayer(layerId)
  unlockBody(layerId)
  window.removeEventListener('keydown', onWindowKeydown)
}

watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      lockBody(layerId)
      window.addEventListener('keydown', onWindowKeydown)
      await nextTick()
      if (!props.modelValue) return
      const layerRoot = drawerRef.value?.parentElement
      if (layerRoot instanceof HTMLElement) registerDrawerLayer(layerId, layerRoot)
      activate()
      return
    }
    releaseDrawer()
    deactivate()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  releaseDrawer()
  deactivate()
})
</script>
